import { useRef, useState } from "react";
import { toast } from "sonner";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Link2, Table2, ImagePlus, Megaphone, Rows3, Columns3, Trash2, Type, Undo2, Redo2, Loader2 } from "lucide-react";
import { formatError } from "@/lib/api";
import { uploadImage } from "./blogApi";
import { FieldDialog } from "./FieldDialog";

const B = ({ icon: I, label, onClick, active, testid, disabled }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} disabled={disabled} title={label} aria-label={label} data-testid={testid}
    className={`rounded-lg p-2 transition-colors disabled:opacity-40 ${active ? "bg-[#7C3AED] text-white" : "text-[#13052A] hover:bg-violet-100"}`}><I className="h-4 w-4" /></button>
);
const Sep = () => <span className="mx-1 h-6 w-px bg-violet-200" />;

export const EditorToolbar = ({ editor }) => {
  const file = useRef(null);
  const [dialog, setDialog] = useState(null);
  const [uploading, setUploading] = useState(false);
  if (!editor) return null;
  const c = () => editor.chain().focus();
  const inTable = editor.isActive("table");
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try { const src = await uploadImage(f); setDialog({ kind: "image", src }); } catch (ex) { toast.error(formatError(ex)); }
    setUploading(false);
  };
  const openLink = () => setDialog({ kind: "link", href: editor.getAttributes("link").href || "" });
  return (
    <div className="sticky top-0 z-20 flex flex-wrap items-center gap-0.5 border-b border-violet-100 bg-white/95 px-3 py-2 backdrop-blur" data-testid="editor-toolbar">
      <B icon={Type} label="Paragraph" active={editor.isActive("paragraph")} onClick={() => c().setParagraph().run()} testid="tb-paragraph" />
      <B icon={Heading2} label="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => c().toggleHeading({ level: 2 }).run()} testid="tb-h2" />
      <B icon={Heading3} label="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => c().toggleHeading({ level: 3 }).run()} testid="tb-h3" />
      <Sep />
      <B icon={Bold} label="Bold" active={editor.isActive("bold")} onClick={() => c().toggleBold().run()} testid="tb-bold" />
      <B icon={Italic} label="Italic" active={editor.isActive("italic")} onClick={() => c().toggleItalic().run()} testid="tb-italic" />
      <B icon={Link2} label="Link" active={editor.isActive("link")} onClick={openLink} testid="tb-link" />
      <Sep />
      <B icon={List} label="Bullet list" active={editor.isActive("bulletList")} onClick={() => c().toggleBulletList().run()} testid="tb-bullets" />
      <B icon={ListOrdered} label="Numbered list" active={editor.isActive("orderedList")} onClick={() => c().toggleOrderedList().run()} testid="tb-numbers" />
      <Sep />
      <B icon={uploading ? Loader2 : ImagePlus} label="Upload image" disabled={uploading} onClick={() => file.current?.click()} testid="tb-image" />
      {editor.isActive("image") && <button type="button" onClick={() => setDialog({ kind: "alt", alt: editor.getAttributes("image").alt || "" })} className="rounded-lg px-2 py-1.5 text-xs font-bold text-[#581C87] hover:bg-violet-100" data-testid="tb-image-alt">Alt text</button>}
      <B icon={Table2} label="Insert table" onClick={() => c().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} testid="tb-table" />
      {inTable && <>
        <B icon={Rows3} label="Add row" onClick={() => c().addRowAfter().run()} testid="tb-row-add" />
        <B icon={Columns3} label="Add column" onClick={() => c().addColumnAfter().run()} testid="tb-col-add" />
        <button type="button" onClick={() => c().deleteRow().run()} className="rounded-lg px-2 py-1.5 text-xs font-bold text-[#581C87] hover:bg-violet-100" data-testid="tb-row-del">Delete row</button>
        <button type="button" onClick={() => c().deleteColumn().run()} className="rounded-lg px-2 py-1.5 text-xs font-bold text-[#581C87] hover:bg-violet-100" data-testid="tb-col-del">Delete column</button>
        <B icon={Trash2} label="Delete table" onClick={() => c().deleteTable().run()} testid="tb-table-del" />
      </>}
      <B icon={Megaphone} label="Call-to-action block" onClick={() => c().insertContent({ type: "ctaBlock" }).run()} testid="tb-cta" />
      <Sep />
      <B icon={Undo2} label="Undo" onClick={() => c().undo().run()} testid="tb-undo" />
      <B icon={Redo2} label="Redo" onClick={() => c().redo().run()} testid="tb-redo" />
      <input ref={file} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onFile} data-testid="tb-image-input" />

      <FieldDialog open={dialog?.kind === "link"} onClose={() => setDialog(null)} title="Add a link" testid="link-dialog" submitLabel="Apply"
        description="Use /page-name for pages on this website, or a full https:// address for other websites. Leave empty to remove the link."
        fields={[{ key: "href", label: "Link address", value: dialog?.href, autoFocus: true, placeholder: "/seo or https://..." }]}
        onSubmit={({ href }) => {
          const h = href.trim();
          if (!h) { c().extendMarkRange("link").unsetLink().run(); return; }
          const external = /^https?:\/\//.test(h);
          c().extendMarkRange("link").setLink({ href: h, target: external ? "_blank" : null, rel: external ? "noopener noreferrer" : null }).run();
        }} />
      <FieldDialog open={dialog?.kind === "image"} onClose={() => setDialog(null)} title="Describe this image" testid="image-dialog" submitLabel="Insert image"
        description="Alt text describes the image for screen readers and Google."
        fields={[{ key: "alt", label: "Alt text", autoFocus: true, placeholder: "e.g. Freya presenting at PPC Live" }]}
        onSubmit={({ alt }) => { c().setImage({ src: dialog.src, alt: alt.trim() }).run(); }} />
      <FieldDialog open={dialog?.kind === "alt"} onClose={() => setDialog(null)} title="Image alt text" testid="alt-dialog"
        fields={[{ key: "alt", label: "Alt text", value: dialog?.alt, autoFocus: true }]}
        onSubmit={({ alt }) => { c().updateAttributes("image", { alt: alt.trim() }).run(); }} />
    </div>
  );
};
